import { JSX, useEffect, useRef } from 'react';
import './Alert.css';

interface AlertProps {
    title: string;
    message: string|JSX.Element;
    onClose: () => void;
    buttonText?: string;
}

function Alert({
  title,
  message,
  onClose,
  buttonText = 'Close',
}: AlertProps) {
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <>
      <div className="nhs-alert-overlay" data-testid="alert-overlay" />
      <div
        ref={alertRef}
        id="nhs-alert"
        className="nhs-alert"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="nhs-alert-title"
        tabIndex={-1}
        data-testid="alert-dialog"
      >
        <div
          role="region"
          aria-label="Notification"
          aria-live="assertive"
          data-testid="alert-container"
        >
          <button
            type="button"
            className="nhs-alert__close-btn"
            onClick={onClose}
            aria-label="Close alert"
            data-testid="alert-close-button"
          >
            <span aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-x-circle"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </span>
          </button>
          <div
            className="nhsuk-notification-banner__content"
            aria-label="Alert content"
            data-testid="alert-content"
          >
            <h2
              className="nhs-alert__title"
              id="nhs-alert-title"
              data-testid="alert-title"
            >
              {title}
            </h2>
            <div className="nhs-alert__divider" />
            <div
              className="nhs-alert__message"
              id="nhs-alert-message"
              data-testid="alert-message"
            >
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
            <div
              className="nhs-alert__button-container"
              aria-label="Action buttons"
              data-testid="alert-button-container"
            >
              <button
                type="button"
                className="nhsuk-button"
                onClick={onClose}
                aria-label={buttonText}
                data-testid="alert-action-button"
              >
                {buttonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Alert;
